// Score Visualization Components
import React from 'react';

/**
 * Team Score Visualization - Shows range and average clearly
 */
export const TeamScoreVisualization = ({ 
  average, 
  min, 
  max, 
  color, 
  showLegend = false 
}) => {
  const rangeLeft = (min / 5) * 100;
  const rangeWidth = ((max - min) / 5) * 100;
  const avgPosition = (average / 5) * 100;
  
  return (
    <div className="mt-4">
      {/* Legend */}
      {showLegend && (
        <div className="flex items-center gap-6 mb-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-8 h-3 rounded-sm opacity-30" style={{ backgroundColor: color }} />
            <span className="text-pp-text">Response range (min to max)</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full border-4"
              style={{ borderColor: color, backgroundColor: 'white' }}
            />
            <span className="text-pp-text">Team average</span>
          </div>
        </div>
      )}
      
      {/* Scale with markers */}
      <div className="relative">
        {/* Background track */}
        <div className="h-4 bg-pp-rule rounded-full relative overflow-visible">
          
          {/* Range interval - shaded band */}
          <div 
            className="absolute h-full rounded-full"
            style={{ 
              left: `${rangeLeft}%`, 
              width: `${Math.max(rangeWidth, 2)}%`,
              backgroundColor: color,
              opacity: 0.25
            }}
          />
          
          {/* Range bracket lines */}
          <div 
            className="absolute top-1/2 flex flex-col items-center"
            style={{ 
              left: `${rangeLeft}%`, 
              transform: 'translateX(-50%) translateY(-50%)' 
            }}
          >
            <div 
              className="w-0.5 h-8 rounded-full" 
              style={{ backgroundColor: color, opacity: 0.6 }} 
            />
          </div>
          <div 
            className="absolute top-1/2 flex flex-col items-center"
            style={{ 
              left: `${rangeLeft + rangeWidth}%`, 
              transform: 'translateX(-50%) translateY(-50%)' 
            }}
          >
            <div 
              className="w-0.5 h-8 rounded-full" 
              style={{ backgroundColor: color, opacity: 0.6 }} 
            />
          </div>
          
          {/* Average marker - prominent circle */}
          <div 
            className="absolute top-1/2 w-6 h-6 rounded-full border-4 shadow-lg z-10"
            style={{ 
              left: `${avgPosition}%`, 
              transform: 'translateX(-50%) translateY(-50%)',
              borderColor: color,
              backgroundColor: 'white',
              boxShadow: `0 0 0 2px ${color}40, 0 2px 8px rgba(0,0,0,0.3)`
            }}
          />
        </div>
        
        {/* Scale labels */}
        <div className="flex justify-between mt-2 text-xs text-pp-text-light px-1">
          <span>1</span>
          <span>2</span>
          <span>3</span>
          <span>4</span>
          <span>5</span>
        </div>
        
        {/* Value labels below */}
        <div className="relative h-6 mt-1">
          {/* Min label */}
          <div 
            className="absolute text-xs font-medium"
            style={{ 
              left: `${rangeLeft}%`, 
              transform: 'translateX(-50%)',
              color: color, 
              opacity: 0.8 
            }}
          >
            {min.toFixed(1)}
          </div>
          {/* Average label */}
          <div 
            className="absolute text-sm font-bold"
            style={{ 
              left: `${avgPosition}%`, 
              transform: 'translateX(-50%)',
              color: color 
            }}
          >
            {average.toFixed(1)}
          </div>
          {/* Max label */}
          <div 
            className="absolute text-xs font-medium"
            style={{ 
              left: `${rangeLeft + rangeWidth}%`, 
              transform: 'translateX(-50%)',
              color: color, 
              opacity: 0.8 
            }}
          >
            {max.toFixed(1)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual Score vs Team Visualization
 */
export const IndividualVsTeamVisualization = ({ 
  myScore, 
  teamAverage, 
  teamMin, 
  teamMax, 
  color 
}) => {
  const myPosition = (myScore / 5) * 100;
  const teamAvgPosition = (teamAverage / 5) * 100;
  const rangeLeft = (teamMin / 5) * 100;
  const rangeWidth = ((teamMax - teamMin) / 5) * 100;
  
  return (
    <div className="mt-3">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-2 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-pp-text">Your reading</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-2 rounded-sm bg-pp-text-light opacity-40" />
          <span className="text-pp-text">Team range</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-0.5 h-4 bg-pp-ink rounded-full" />
          <span className="text-pp-text">Team avg</span>
        </div>
      </div>
      
      <div className="relative">
        {/* Track */}
        <div className="h-4 bg-pp-rule rounded-full relative">

          {/* Team range band */}
          <div
            className="absolute h-full rounded-full bg-pp-text-light opacity-30"
            style={{ 
              left: `${rangeLeft}%`, 
              width: `${Math.max(rangeWidth, 2)}%` 
            }}
          />
          
          {/* Team average line */}
          <div 
            className="absolute top-0 h-full w-0.5 bg-pp-ink opacity-60"
            style={{ 
              left: `${teamAvgPosition}%`, 
              transform: 'translateX(-50%)' 
            }}
          />
          
          {/* My score - solid filled circle */}
          <div 
            className="absolute top-1/2 w-5 h-5 rounded-full shadow-lg z-10"
            style={{ 
              left: `${myPosition}%`, 
              transform: 'translateX(-50%) translateY(-50%)',
              backgroundColor: color,
              boxShadow: `0 0 0 3px ${color}40, 0 2px 6px rgba(0,0,0,0.3)`
            }}
          />
        </div>
        
        {/* Scale */}
        <div className="flex justify-between mt-1 text-xs text-pp-text-light px-1">
          <span>1</span>
          <span>2</span>
          <span>3</span>
          <span>4</span>
          <span>5</span>
        </div>
        
        {/* Value callouts */}
        <div className="flex items-center justify-between mt-2 text-xs">
          <div className="flex items-center gap-1">
            <span className="font-bold" style={{ color }}>You: {myScore.toFixed(1)}</span>
          </div>
          <div className="text-pp-text">
            Team: {teamMin.toFixed(1)} –
            <span className="text-pp-ink font-medium"> {teamAverage.toFixed(1)} </span>
            – {teamMax.toFixed(1)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Overall Score Gauge
 */
export const ScoreGauge = ({ score, interpretation, size = 'large' }) => {
  const sizeClasses = {
    small: 'w-20 h-20 text-2xl',
    medium: 'w-28 h-28 text-3xl',
    large: 'w-32 h-32 text-4xl'
  };

  return (
    <div className="flex flex-col items-center">
      <div 
        className={`${sizeClasses[size]} rounded-full border-4 flex items-center justify-center`}
        style={{ borderColor: interpretation.color }}
      >
        <div className="text-center">
          <div className="font-bold text-pp-ink">{score.toFixed(1)}</div>
          <div className="text-sm text-pp-text">out of 5</div>
        </div>
      </div>
      <div 
        className="mt-3 px-4 py-1 rounded-full text-sm font-medium"
        style={{ 
          backgroundColor: `${interpretation.color}20`, 
          color: interpretation.color 
        }}
      >
        {interpretation.label}
      </div>
    </div>
  );
};

/**
 * Response Distribution Chart
 */
export const DistributionChart = ({ distribution, color }) => {
  const total = distribution.reduce((a, b) => a + b, 0) || 1;
  
  return (
    <div className="flex gap-1 h-8">
      {distribution.map((count, i) => (
        <div key={i} className="flex-1 flex flex-col justify-end">
          <div 
            className="rounded-t transition-all"
            style={{ 
              height: `${Math.max((count / total) * 100, 4)}%`,
              backgroundColor: color,
              opacity: 0.3 + (count / total) * 0.7
            }}
          />
          <span className="text-xs text-pp-text-light text-center mt-1">{i + 1}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Progress Bar
 */
export const ProgressBar = ({ current, total, color = '#6B9E9B' }) => {
  const percentage = (current / total) * 100;

  return (
    <div className="h-2 bg-pp-rule rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-300"
        style={{ 
          width: `${percentage}%`,
          backgroundColor: color
        }}
      />
    </div>
  );
};
